import React from 'react';
import { Box, Skeleton } from '@mui/material';
import AppStyles from '../styles/AppStyles';

const SkeletonLoader = ({ 
  variant = 'rectangular', 
  width = '100%', 
  height = 20, 
  count = 1, 
  spacing = 1,
  animation = 'wave',
  sx = {},
  ...props 
}) => {
  const skeletons = Array.from({ length: count }, (_, index) => (
    <Skeleton
      key={index}
      variant={variant}
      width={width}
      height={height}
      animation={animation}
      sx={{
        backgroundColor: 'rgba(0, 0, 0, 0.08)',
        borderRadius: AppStyles.borderRadius.sm,
        '&::after': {
          background: 'linear-gradient(90deg, transparent, rgba(0, 0, 0, 0.15), transparent)',
        },
        ...sx,
      }}
      {...props}
    />
  ));

  if (count === 1) {
    return skeletons[0];
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: spacing }}>
      {skeletons}
    </Box>
  );
};

// Predefined skeleton patterns for common use cases
export const CardSkeleton = ({ count = 1, spacing = 2 }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: spacing }}>
    {Array.from({ length: count }, (_, index) => (
      <Box
        key={index}
        sx={{
          p: 3,
          borderRadius: AppStyles.borderRadius.lg,
          backgroundColor: 'transparent',
        }}
      >
        <SkeletonLoader variant="circular" width={40} height={40} sx={{ mb: 2 }} />
        <SkeletonLoader count={2} height={16} spacing={1} />
        <SkeletonLoader width="60%" height={14} sx={{ mt: 1 }} />
      </Box>
    ))}
  </Box>
);

export const TableSkeleton = ({ rows = 5, columns = 4 }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
    {/* Header */}
    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
      {Array.from({ length: columns }, (_, index) => (
        <SkeletonLoader key={index} width="100%" height={20} />
      ))}
    </Box>
    {/* Rows */}
    {Array.from({ length: rows }, (_, rowIndex) => (
      <Box key={rowIndex} sx={{ display: 'flex', gap: 2 }}>
        {Array.from({ length: columns }, (_, colIndex) => (
          <SkeletonLoader key={colIndex} width="100%" height={16} />
        ))}
      </Box>
    ))}
  </Box>
);

export const ListSkeleton = ({ items = 5, showAvatar = false }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
    {Array.from({ length: items }, (_, index) => (
      <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {showAvatar && (
          <SkeletonLoader variant="circular" width={40} height={40} />
        )}
        <Box sx={{ flex: 1 }}>
          <SkeletonLoader width="80%" height={16} sx={{ mb: 0.5 }} />
          <SkeletonLoader width="60%" height={14} />
        </Box>
      </Box>
    ))}
  </Box>
);

export const ChartSkeleton = ({ height = 300 }) => (
  <Box sx={{ width: '100%', height }}>
    <SkeletonLoader 
      variant="rectangular" 
      width="100%" 
      height="100%" 
      sx={{ borderRadius: AppStyles.borderRadius.md }}
    />
  </Box>
);

export default SkeletonLoader;
